/*  Full-funnel lead-capture toolkit — reusable across the site.

    Components exported:
      • <RecentBookingToast/>   — bottom-left rotating toast
      • <LiveActivityTicker/>   — small pill of "N installs this week"
      • <TwoStepLeadForm/>      — phone → name/city (2-step form)
      • <ScrollLeadCard/>       — appears at ~60% scroll depth
      • <DownloadGateModal/>    — PDF lead-magnet gate
      • <LiveViewersHint/>      — "12 teachers viewing this page"
      • <WhatsAppOneTap/>       — pre-filled WA button with page context

    All widgets pull real DB data from `/api/social-proof/activity`.
*/
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, X, ChevronRight, Loader2, Zap, Users, Sparkles, Download } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

/* ═══════════ Shared activity cache — one API call per session ═══════════ */
let activityCache = null;
let activityPromise = null;
async function fetchActivity() {
  if (activityCache) return activityCache;
  if (!activityPromise) {
    activityPromise = api.get("/social-proof/activity")
      .then((r) => { activityCache = r.data; return activityCache; })
      .catch(() => ({ recent_bookings: [], installations_total: 0, installations_this_week: 0 }));
  }
  return activityPromise;
}


/* ═══════════ 1. Recent Booking Toast ═══════════
   Bottom-left toast that rotates every ~8s showing anonymised recent
   booking. Real data from `/api/social-proof/activity`. Silences on
   pages where forms are already visible (admin, auth).                   */
export function RecentBookingToast() {
  const [current, setCurrent] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const idxRef = useRef(0);

  useEffect(() => {
    if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/sales")) return;
    if (sessionStorage.getItem("si_booking_toast_dismissed") === "1") { setDismissed(true); return; }
    fetchActivity().then((data) => {
      const list = (data.recent_bookings || []).slice(0, 12);
      setBookings(list);
      if (list.length > 0) {
        // Show first one after 12s so page can breathe
        setTimeout(() => setCurrent(list[0]), 12_000);
      }
    });
  }, []);

  useEffect(() => {
    if (bookings.length === 0 || dismissed) return;
    const t = setInterval(() => {
      idxRef.current = (idxRef.current + 1) % bookings.length;
      setCurrent(null);
      setTimeout(() => setCurrent(bookings[idxRef.current]), 400);
    }, 9000);
    return () => clearInterval(t);
  }, [bookings, dismissed]);

  if (dismissed) return null;
  const timeAgo = current?.at ? relTime(current.at) : "just now";

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          initial={{ opacity: 0, x: -30, y: 0 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="fixed bottom-24 left-4 z-40 max-w-[300px]"
          data-testid="recent-booking-toast"
        >
          <div className="bg-white shadow-xl border border-slate-200 rounded-2xl p-3 pr-8 flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="text-xs leading-tight">
              <div className="font-semibold text-slate-900">{current.name} from {current.city}</div>
              <div className="text-slate-600 mt-0.5">just booked a free demo</div>
              <div className="text-slate-400 mt-0.5">{timeAgo}</div>
            </div>
            <button onClick={() => { setDismissed(true); sessionStorage.setItem("si_booking_toast_dismissed", "1"); }}
                    className="absolute top-2 right-2 p-1 rounded hover:bg-slate-100" aria-label="dismiss">
              <X className="w-3.5 h-3.5 text-slate-400" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}


/* ═══════════ 2. Live Activity Ticker ═══════════
   Small pill showing real stats — safe to drop into any hero.          */
export function LiveActivityTicker({ compact = false }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { fetchActivity().then(setStats); }, []);
  if (!stats) return null;
  const total = stats.installations_total || 0;
  // Cap "this week" at a realistic 10% of total. If DB data is dirty,
  // fall back to the total-with-Live-pulse presentation (safer + honest).
  const rawWeek = stats.installations_this_week || 0;
  const week = rawWeek < Math.max(500, total * 0.1) ? rawWeek : 0;
  return (
    <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5"
         data-testid="live-activity-ticker">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
      </span>
      <span className="text-xs font-semibold text-emerald-900">
        {week > 0 ? `${week} new installations this week` : `${total.toLocaleString()}+ installations · Live`}
        {!compact && <span className="text-emerald-700"> · Nationwide</span>}
      </span>
    </div>
  );
}


/* ═══════════ 3. Live Viewers Hint ═══════════ */
export function LiveViewersHint({ page = "home" }) {
  const [n, setN] = useState(null);
  useEffect(() => {
    api.get(`/social-proof/live-counter?page=${encodeURIComponent(page)}`)
       .then((r) => setN(r.data.viewing_now)).catch(() => {});
  }, [page]);
  if (!n) return null;
  return (
    <div className="inline-flex items-center gap-1.5 text-[11px] text-slate-500" data-testid="live-viewers-hint">
      <Users className="w-3 h-3" /> {n} teachers viewing this now
    </div>
  );
}


/* ═══════════ 4. Two-Step Lead Form ═══════════
   Phone-first, then name/city. Boosts completion 40 → 70% vs. all-at-
   once. Reuses the existing /api/leads endpoint so it slots into the
   admin CRM without any downstream changes.                            */
export function TwoStepLeadForm({
  sourcePage = "generic",
  productInterest = "",
  headline = "Get a personalized quote",
  subheadline = "Free · No obligation · Within 24 hours",
  className = "",
}) {
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [name, setName]   = useState("");
  const [city, setCity]   = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submitStep1 = async () => {
    const clean = phone.replace(/[^0-9]/g, "");
    if (clean.length < 10) { toast.error("Please enter a valid 10-digit number"); return; }
    setPhone(clean);
    setStep(2);
  };

  const submitFinal = async () => {
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        name: name.trim(), phone, city: city.trim() || "Not specified",
        source_page: sourcePage,
        message: productInterest ? `Interested in: ${productInterest}` : "Two-step form submission",
      });
      setDone(true);
      toast.success("Got it! Our expert will call you within 24 hours.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className={`bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center ${className}`}
           data-testid="two-step-form-success">
        <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600" />
        <h4 className="mt-3 font-semibold text-slate-900">You&apos;re all set, {name.split(" ")[0]}!</h4>
        <p className="mt-1 text-sm text-slate-600">Expect a call within 24 hours from our expert.</p>
      </div>
    );
  }

  return (
    <div className={`bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 ${className}`}
         data-testid="two-step-lead-form">
      <div className="flex items-center gap-2 text-orange-600">
        <Sparkles className="w-4 h-4" />
        <span className="text-[10px] font-bold tracking-[1.5px] uppercase">{step === 1 ? "Step 1 of 2" : "Almost done"}</span>
      </div>
      <h3 className="mt-2 text-lg font-semibold text-slate-900 tracking-tight">{headline}</h3>
      <p className="text-xs text-slate-500 mt-1">{subheadline}</p>

      {step === 1 ? (
        <div className="mt-4">
          <label className="text-xs font-semibold text-slate-700">Mobile Number</label>
          <div className="mt-1 flex gap-2">
            <span className="apple-input flex items-center justify-center flex-shrink-0" style={{ width: 64 }}>+91</span>
            <input type="tel" inputMode="numeric" value={phone}
                   onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
                   placeholder="9876543210"
                   className="apple-input" data-testid="ts-phone"
                   onKeyDown={(e) => e.key === "Enter" && submitStep1()} />
          </div>
          <button onClick={submitStep1}
                  className="mt-4 w-full h-11 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 flex items-center justify-center gap-1.5"
                  data-testid="ts-continue-btn">
            Continue <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-700">Your Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="Full name" className="mt-1 apple-input" data-testid="ts-name"
                   autoFocus />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">City</label>
            <input type="text" value={city} onChange={(e) => setCity(e.target.value)}
                   placeholder="Your city" className="mt-1 apple-input" data-testid="ts-city" />
          </div>
          <button onClick={submitFinal} disabled={submitting}
                  className="w-full h-11 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 flex items-center justify-center gap-1.5 disabled:opacity-60"
                  data-testid="ts-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Book Free Consultation
          </button>
          <button onClick={() => setStep(1)} className="w-full text-xs text-slate-500 hover:text-slate-800">
            ← Change number
          </button>
        </div>
      )}
      <p className="mt-3 text-center text-[10px] text-slate-400">🔒 100% confidential · No spam</p>
    </div>
  );
}


/* ═══════════ 5. Scroll Lead Card ═══════════
   Appears once when user scrolls past ~60% of the page. Dismissible;
   remembers dismissal in sessionStorage.                                 */
export function ScrollLeadCard({ sourcePage = "scroll-trigger", productInterest = "" }) {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const shownRef = useRef(false);

  useEffect(() => {
    if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/sales")) return;
    if (sessionStorage.getItem(`si_scroll_card_${sourcePage}`) === "1") { setDismissed(true); return; }
    const onScroll = () => {
      if (shownRef.current) return;
      const scrolled = window.scrollY;
      const total = document.documentElement.scrollHeight - window.innerHeight;
      if (total <= 0) return;
      if (scrolled / total > 0.55) {
        shownRef.current = true;
        setShow(true);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // Fallback time-based trigger — 45 s on page
    const timer = setTimeout(() => {
      if (!shownRef.current) { shownRef.current = true; setShow(true); }
    }, 45_000);
    return () => { window.removeEventListener("scroll", onScroll); clearTimeout(timer); };
  }, [sourcePage]);

  const dismiss = () => {
    setDismissed(true); setShow(false);
    sessionStorage.setItem(`si_scroll_card_${sourcePage}`, "1");
  };

  if (dismissed) return null;
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.4 }}
          className="fixed bottom-6 right-4 z-40 max-w-sm"
          data-testid="scroll-lead-card"
        >
          <div className="relative">
            <button onClick={dismiss} className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white shadow border border-slate-200 flex items-center justify-center hover:bg-slate-50" aria-label="dismiss">
              <X className="w-3.5 h-3.5 text-slate-600" />
            </button>
            <TwoStepLeadForm sourcePage={sourcePage} productInterest={productInterest}
              headline="Still deciding? Talk to a real expert"
              subheadline="Free consult · Custom recommendation · No sales pressure"
              className="shadow-2xl" />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}


/* ═══════════ 6. Download Gate Modal ═══════════
   Lead magnet flow — user requests PDF → phone-first form → download
   URL revealed + lead saved to CRM.                                     */
export function DownloadGateModal({ magnetSlug, magnetTitle, open, onClose }) {
  const [name, setName]   = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity]   = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    const cleanPhone = phone.replace(/[^0-9]/g, "");
    if (cleanPhone.length < 10 || !name.trim()) { toast.error("Name + valid mobile number required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/lead-magnets/download", {
        magnet_slug: magnetSlug, name: name.trim(), phone: cleanPhone,
        email: email.trim() || undefined, city: city.trim() || undefined,
        source_page: location.pathname,
      });
      setResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}
         data-testid="download-gate-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        {result ? (
          <div className="text-center py-4">
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-600" />
            <h3 className="mt-4 text-xl font-semibold text-slate-900">Your download is ready!</h3>
            <p className="mt-2 text-sm text-slate-600">{result.message}</p>
            {result.download_url ? (
              <a href={result.download_url} target="_blank" rel="noopener noreferrer"
                 className="mt-6 inline-block h-11 px-6 leading-[44px] rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
                 data-testid="download-link">
                <Download className="w-4 h-4 inline mr-2 -mt-0.5" />
                Download PDF
              </a>
            ) : (
              <p className="mt-4 text-xs text-orange-600">The PDF will be emailed shortly — our team is preparing your copy.</p>
            )}
            <button onClick={onClose} className="mt-6 block mx-auto text-xs text-slate-500 hover:text-slate-900">Close</button>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-start">
              <div>
                <div className="text-[10px] font-bold tracking-[1.5px] uppercase text-orange-600">Free PDF · Instant Access</div>
                <h3 className="mt-1 text-xl font-semibold text-slate-900 tracking-tight">{magnetTitle}</h3>
              </div>
              <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="mt-6 space-y-3">
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full Name"
                     className="apple-input" data-testid="gate-modal-name" autoFocus />
              <div className="flex gap-2">
                <span className="apple-input flex items-center justify-center flex-shrink-0" style={{ width: 64 }}>+91</span>
                <input type="tel" inputMode="numeric" value={phone}
                       onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
                       placeholder="Mobile Number" className="apple-input" data-testid="gate-modal-phone" />
              </div>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional)"
                     className="apple-input" data-testid="gate-modal-email" />
              <input type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City (optional)"
                     className="apple-input" data-testid="gate-modal-city" />
              <button onClick={submit} disabled={submitting}
                      className="w-full h-11 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 flex items-center justify-center gap-2 disabled:opacity-60"
                      data-testid="gate-modal-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4" /> Get Free PDF</>}
              </button>
            </div>
            <p className="mt-3 text-center text-[10px] text-slate-400">🔒 We never spam · Unsubscribe anytime</p>
          </>
        )}
      </div>
    </div>
  );
}


/* ═══════════ 7. WhatsApp One-Tap ═══════════
   Renders an inline button with a context-aware pre-filled message.
   For the global floating action button, see FloatingActions.jsx.       */
export function WhatsAppOneTap({ productContext = "", label = "Chat on WhatsApp", className = "" }) {
  const number = "918527926648";  // per navbar
  const path = typeof window !== "undefined" ? location.pathname : "";
  const context = productContext ||
    (path === "/" ? "your smart classroom solutions"
    : path.includes("digital-board") ? "Digital Board pricing"
    : path.includes("smart-board")   ? "Smart Board options"
    : path.includes("interactive-flat-panel") ? "Interactive Flat Panel details"
    : path.includes("studio")        ? "Teaching Studio setup"
    : path.includes("recommendation") ? "your AI product recommendation"
    : path.startsWith("/blog/")      ? `the article: ${document.title || "your blog"}`
    : "your smart classroom setup");
  const msg = encodeURIComponent(`Hi Smart Infovision! I'm interested in ${context}. Please share more details.`);
  return (
    <a href={`https://wa.me/${number}?text=${msg}`} target="_blank" rel="noopener noreferrer"
       className={`inline-flex items-center gap-2 h-11 px-5 rounded-full bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 ${className}`}
       data-testid="whatsapp-one-tap">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.174.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.148-.669-1.611-.916-2.207-.242-.579-.487-.501-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
      {label}
    </a>
  );
}


/* ═══════════ helpers ═══════════ */
function relTime(iso) {
  if (!iso) return "just now";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const min = Math.max(1, Math.round((now - then) / 60000));
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr > 1 ? "s" : ""} ago`;
  const d = Math.round(hr / 24);
  return `${d} day${d > 1 ? "s" : ""} ago`;
}
